import * as React from 'react';
import * as _ from 'lodash';
import FormDialog, {SpecificFormDialogProps} from 'cl-dialogs/FormDialog';
import FormInput from 'cl-form/FormInput';
import {ACTION_DISPLAY_TYPES} from '../../ActionsManager/ActionsManagerConsts';
import {GENERAL_ACTIONS_TYPES} from '../../ActionsManager/ActionsTypes';
import GeneralActions from '../../ActionsManager/GeneralActions/GeneralActions';
import {CreateLinkProductParams} from 'cl-api';
import GridFormHeader from 'cl-form/GridFormHeader';
import GridFormLabel from 'cl-form/GridFormLabel';
import {Omit} from 'react-redux';
import FormSelect from 'cl-form/FormSelect/FormSelect';
import {useMemo} from 'react';

const NO_SKU_NAME = '-';
const DONT_CREATE_VALUE = 'dontCreate';
const DONT_LINK_VALUE = 'dontLink';

export interface LinkToProductFormData extends CreateLinkProductParams {
  SKU: string;
}

export interface LinkToProductDialogProps
  extends Omit<SpecificFormDialogProps<LinkToProductFormData, LinkToProductFormData>, 'variant'> {
  variant?: 'link' | 'create';
  variations?: any[];
}

const LinkToProductDialog: React.FunctionComponent<LinkToProductDialogProps> = props => {
  const {sellerId, SKU, channel, listingId, variant, variations, ...otherProps} = props;
  const createProduct = useMemo(() => variant === 'create', [variant]);
  const options = useMemo(
    () =>
      createProduct
        ? [{label: "Don't create", value: DONT_CREATE_VALUE}]
        : [{label: "Don't link", value: DONT_LINK_VALUE}],
    [createProduct]
  );

  const fields: any = useMemo(
    () =>
      variations
        ? [
            {
              component: GridFormHeader,
              name: 'header',
              optional: true,
              props: {leftText: `${channel} SKU`, rightText: `Product SKU`},
            },
            {
              component: GridFormLabel,
              label: SKU ? SKU : NO_SKU_NAME,
              name: 'SKU',
              explanationText: 'Parent listing',
              optional: !createProduct,
              markOptional: false,
              props: {inputComponent: createProduct ? FormInput : null, showArrow: false},
            },
            ..._.map(variations, (variation, variationId) => {
              const specifics = _.join(
                _.map(variation.ItemSpecifics, (value, name) => {
                  return `${name}: ${value}`;
                }),
                ' | '
              );
              const disabled = !variation.SKU;
              return {
                component: GridFormLabel,
                label: variation.SKU ? variation.SKU : NO_SKU_NAME,
                name: variationId,
                explanationText: specifics,
                props: {
                  inputComponent: FormSelect,
                  options: disabled ? [{label: "Create, don't link", value: DONT_LINK_VALUE}] : options,
                  creatable: true,
                  disabled,
                },
              };
            }),
          ]
        : [
            {
              component: FormInput,
              name: 'SKU',
              label: createProduct ? 'New product SKU' : 'Existing product SKU',
              props: {autoFocus: _.isNil(SKU)},
            },
          ],
    [SKU, variations, createProduct]
  );

  const text = useMemo(
    () =>
      createProduct ? (
        <>
          Create a new product and link this listing to it
          <br />
          Already have this product in your catalog?{' '}
          <GeneralActions
            types={[GENERAL_ACTIONS_TYPES.OPEN_LINK_TO_PRODUCT_DIALOG]}
            displayType={ACTION_DISPLAY_TYPES.INLINE_BUTTON}
            additionalActionData={props}
          />
        </>
      ) : (
        <>
          Link this listing to an existing product in your products catalog.
          <br />
          Your product and listing may have different SKU's
          <br />
          Can't find a matching product in your catalog?{' '}
          <GeneralActions
            types={[GENERAL_ACTIONS_TYPES.OPEN_CREATE_PRODUCT_FROM_LISTING_DIALOG]}
            displayType={ACTION_DISPLAY_TYPES.INLINE_BUTTON}
            additionalActionData={props}
          />
        </>
      ),
    [createProduct]
  );

  const actionName = useMemo(
    () => (createProduct ? GENERAL_ACTIONS_TYPES.CREATE_PRODUCT_FROM_LISTING : GENERAL_ACTIONS_TYPES.LINK_TO_PRODUCT),
    [createProduct]
  );
  const title = useMemo(
    () => (createProduct ? 'Create new product from a listing' : 'Link listing to existing product in your catalog'),
    [createProduct]
  );
  const variationsInitialValues = useMemo(() => {
    if (!variations) {
      return {};
    }
    return _.fromPairs(
      _.map(variations, (variation, variationId) => [variationId, variation.SKU ? variation.SKU : DONT_LINK_VALUE])
    );
  }, []);

  return (
    <FormDialog<LinkToProductFormData>
      formName={actionName}
      initialValues={{
        sellerId,
        SKU,
        listingSKU: SKU,
        channel,
        listingId,
        ...variationsInitialValues,
      }}
      title={title}
      text={text}
      fields={fields}
      actions={
        <GeneralActions
          types={[actionName]}
          displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
          additionalActionData={{variations}}
        />
      }
      {...otherProps}
    />
  );
};

LinkToProductDialog.defaultProps = {
  variant: 'link',
};

export default LinkToProductDialog;
